import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';

export const AddOnBillingCycles = {
    Year: 'year',
    Month: 'month',
} as const;

export type AddOnBillingCycle = (typeof AddOnBillingCycles)[keyof typeof AddOnBillingCycles];

export const AddOnGroups = {
    OTATraffic: 'ota_traffic',
} as const;

export type AddOnGroup = (typeof AddOnGroups)[keyof typeof AddOnGroups];

export const SubscriptionExtraTypes = {
    OtaTrafficLimit: 'ota_traffic_limit',
} as const;

export type SubscriptionExtraType = (typeof SubscriptionExtraTypes)[keyof typeof SubscriptionExtraTypes];

export type OTAPricingOption = {
    id: string;
    unitsIncluded: number;
    price: number;
};

export type AddOnStatusSubscriptionItem = {
    quantity: number;
    price: {
        unitAmount: number;
        extraType: SubscriptionExtraType;
        extraQuantity: string | number;
    };
};

export type AddOnStatusResponse = {
    billingCycle: AddOnBillingCycle;
    isBiller: boolean;
    isInvoiced: boolean;
    isTrialPlan: boolean;
    stripeKey: string;
    subscriptionItems: AddOnStatusSubscriptionItem[];
};

export type AddOnActivateRequest = {
    priceId: string | number;
};

export type AddOnActivateResponse = {
    result: string;
};

export type SubscribePaymentSetupResponse = {
    stripePaymentMethodId: string;
    paymentIntentClientSecret: string;
};

export const isAddOnActivateResponse = (
    res: AddOnActivateResponse | SubscribePaymentSetupResponse,
): res is AddOnActivateResponse => 'result' in res;

export type AddOnUpcomingInvoiceResponse = {
    customAllowanceType: string;
    fullPackagePrice: number;
    fullPackageUnitsCount: number;
    fullSubscriptionPackagesPrice: number;
    fullSubscriptionUnitsCount: number;
    nextBillingCycleTimestamp: number;
    proratedPackagePrice: number;
};

const addOnBillingApi = createApi({
    reducerPath: 'addOnBilling',
    baseQuery: fetchBaseQuery(),
    endpoints: (builder) => ({
        getPrices: builder.query<OTAPricingOption[], { addOnGroup: AddOnGroup }>({
            query: ({ addOnGroup }) => ({
                method: 'GET',
                url: `/team/billing/extras/prices/${addOnGroup}`,
            }),
        }),
        getStatus: builder.query<AddOnStatusResponse, void>({
            query: () => ({
                method: 'GET',
                url: `/team/billing/extras/status`,
            }),
        }),
        getUpcomingInvoice: builder.query<AddOnUpcomingInvoiceResponse, { priceId: string | number }>({
            query: ({ priceId }) => ({
                method: 'POST',
                url: `/team/billing/extras/prices/upcoming`,
                body: { priceId },
            }),
        }),
        activateAddon: builder.mutation<AddOnActivateResponse | SubscribePaymentSetupResponse, AddOnActivateRequest>({
            query: ({ priceId }) => ({
                method: 'POST',
                url: `/team/billing/extras/activate`,
                body: { priceId },
            }),
        }),
    }),
});

export const {
    useGetPricesQuery,
    useGetStatusQuery,
    useLazyGetUpcomingInvoiceQuery,
    useActivateAddonMutation,
    useGetUpcomingInvoiceQuery,
} = addOnBillingApi;

export default addOnBillingApi;
